import React, { FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik } from 'formik';

import { registerForEvent, getMyCompanyDemographics } from '../../lib/Api/participant/Event';
import { getUserEvent } from '../../lib/Api/User';

import { SelectDropdown, Modal } from '../../components/UI';
import LoadingIndicator from '../../components/UI/LoadingIndicator';

import { participantRegisterDemographics } from '../../assets/img';
import {
  RegisterModalContainer,
  ModalGraphic,
  ModalContent,
  ButtonWrapper,
  Submit,
  Cancel
} from './Styles';
import { DarkBlueBodyS, BoldText, DarkBlueHeaderL } from '../../Themes';

import { ApplicationState } from '../../lib/Store';
import { CompanyDemographic, APIThunkDispatch } from 'src/lib/Types';

export interface RegisterModalProps {
  onCancel: () => void;
  onParticipantCapError: () => void;
}

const RegisterModal: FC<RegisterModalProps> = ({ onCancel, onParticipantCapError }) => {
  const dispatch: APIThunkDispatch = useDispatch();
  const { companyDemographics, event } = useSelector(
    ({ participantEventState, userState }: ApplicationState) => ({
      companyDemographics: participantEventState.companyDemographics,
      event: userState.userEvent
    })
  );

  // The company demographics are loaded fire-and-forget on login, which means the
  // registration modal can open before they've resolved. If a participant submits
  // during that window, `companyDemographics` is still empty, validation passes
  // trivially, and they register without answering any questions. Re-fetch and
  // await here so the form is always seeded with the full question set before the
  // participant is allowed to complete registration.
  const [demographicsLoading, setDemographicsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      await dispatch(getMyCompanyDemographics());
      if (isMounted) setDemographicsLoading(false);
    })();
    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  return (
    <Formik
      initialValues={{
        demographics: companyDemographics.map(cd => ({
          companyDemographicId: cd.companyDemographicId,
          childDemographicAnswer: null,
          childDemographicId: null
        }))
      }}
      validateOnBlur={false}
      validateOnChange
      validateOnMount={false}
      validate={({ demographics }) => {
        const errors: {
          demographics: {
            [key: string]: { error: string };
          };
        } = {
          demographics: {}
        };
        if (demographics.length > 0) {
          demographics.map((demo, index) => {
            if (!demo.childDemographicAnswer && !demo.childDemographicId)
              errors.demographics[index] = { error: 'Each demographic must be answered' };
          });
        }
        if (Object.keys(errors.demographics).length > 0) return errors;
        return {};
      }}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        // Defensive guard: never submit until the company demographics have finished
        // loading, and ensure every company demographic question has an answer in the
        // payload. This closes the race where an empty `companyDemographics` array
        // would let registration succeed with no answers.
        if (
          demographicsLoading ||
          values.demographics.length !== companyDemographics.length ||
          values.demographics.some(
            demo => !demo.childDemographicAnswer && !demo.childDemographicId
          )
        ) {
          return;
        }
        setSubmitting(true);
        const { data, error } = await dispatch(
          registerForEvent({
            eventId: event.eventId,
            demographics: values.demographics
          })
        );
        if (
          error &&
          error.message ===
            'Paid participants for this event has been reached for your company. Please contact your company administrator'
        ) {
          onParticipantCapError();
        } else if (data) {
          await dispatch(getUserEvent());
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, handleSubmit, setFieldValue, isSubmitting }) => {
        const getValue = (demoField: CompanyDemographic): number => {
          const currentAnswer = values.demographics.find(
            ud => ud.companyDemographicId === demoField.companyDemographicId
          );
          if (!currentAnswer) return;
          const value = demoField.options.find(
            o => o.companyDemographicId === currentAnswer.childDemographicId
          );
          return value?.companyDemographicId;
        };
        return (
          <Modal
            overflowVisible
            onClose={onCancel}
            padding="40px"
            top={10}
            titlePadding="0px"
            showHeader={false}
          >
            <RegisterModalContainer>
              <ModalGraphic src={participantRegisterDemographics} />
              <ModalContent>
                <DarkBlueHeaderL>Complete your registration</DarkBlueHeaderL>
                {demographicsLoading ? (
                  <LoadingIndicator />
                ) : (
                  <>
                    {values.demographics.length > 0 ? (
                      <DarkBlueBodyS padding={'0 20% 3rem 0'}>
                        {
                          'Answering the questions below will help place you on the correct leaderboards for the '
                        }
                        <BoldText>{event.name}</BoldText>
                        {' event. '}
                      </DarkBlueBodyS>
                    ) : (
                      <DarkBlueBodyS padding={'0 20% 3rem 0'}>
                        {'Please click Complete to finish registering for '}
                        <BoldText>{event.name}</BoldText>
                        {' or click Cancel to finish your registration at another time.'}
                      </DarkBlueBodyS>
                    )}
                    {companyDemographics.map((demoField, index) => (
                      <div key={index}>
                        <SelectDropdown
                          required
                          data={demoField.options}
                          value={getValue(demoField)}
                          error={errors && errors.demographics && errors.demographics[index]}
                          valueProp="companyDemographicId"
                          displayProp="name"
                          placeholder={'Select a ' + demoField.name}
                          onChange={value => {
                            const currentAnswerIndex = values.demographics.findIndex(
                              ud => ud.companyDemographicId === demoField.companyDemographicId
                            );
                            setFieldValue(`demographics[${currentAnswerIndex}]`, {
                              companyDemographicId: demoField.companyDemographicId,
                              childDemographicAnswer: demoField.options.find(
                                o => o.companyDemographicId === value
                              )?.name,
                              childDemographicId: demoField.options.find(
                                o => o.companyDemographicId === value
                              )?.companyDemographicId
                            });
                          }}
                        />
                        <br></br>
                      </div>
                    ))}
                  </>
                )}
                <ButtonWrapper>
                  <Cancel onClick={onCancel} type="button" variant="cancel">
                    Cancel
                  </Cancel>
                  <Submit
                    type="button"
                    onClick={() => handleSubmit()}
                    variant="report"
                    disabled={isSubmitting || demographicsLoading}
                  >
                    Complete
                  </Submit>
                </ButtonWrapper>
              </ModalContent>
            </RegisterModalContainer>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default RegisterModal;
